import { useQuery } from "@tanstack/react-query";
import { getDataStudioTableProfile } from "../services/dataStudio/dataStudioTablesApi";
import { getApiErrorMessage } from "../services/apiErrors";
import { queryKeys } from "../services/queryKeys";
import type { ConnectorAssetIdentity } from "../services/dataStudio/dataStudioAssets.types";
import type { GetDataStudioTableProfileResponse } from "../services/dataStudio/dataStudioTables.types";

export interface UseDataStudioTableProfileResult {
  profile: GetDataStudioTableProfileResponse | undefined;
  isLoading: boolean;
  /** Backend's own reason when the asset or its columns fail to load. */
  errorMessage: string | null;
  refetch: () => void;
}

/**
 * Dataset header and grid columns for the selected asset.
 *
 * Kept separate from the rows query so the header and column metadata can
 * paint while row data is still streaming — the two come from different
 * endpoints and there is no reason to make the faster one wait.
 *
 * Disabled until an asset is selected, which is why the identity is nullable
 * rather than the caller guarding every render.
 */
export const useDataStudioTableProfile = (
  identity: ConnectorAssetIdentity | null,
): UseDataStudioTableProfileResult => {
  const connectionId = identity?.connectionId ?? "";
  const assetId = identity?.assetId ?? "";

  const query = useQuery<GetDataStudioTableProfileResponse>({
    queryKey: queryKeys.dataStudio.tableProfile(
      connectionId || "none",
      assetId || "none",
    ),
    queryFn: () => getDataStudioTableProfile({ connectionId, assetId }),
    enabled: Boolean(connectionId && assetId),
  });

  return {
    profile: query.data,
    isLoading: query.isLoading,
    errorMessage: query.isError
      ? getApiErrorMessage(query.error, "Unable to load the table profile.")
      : null,
    refetch: () => {
      query.refetch();
    },
  };
};
